import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useAuth } from "auth/AuthContext";
import { useActionWithPath } from "utils/query";

export interface ApiGatewayToggleDialogProps {
  clusterDetail: any;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  refetchCluster?: () => void;
  isEnabling: boolean; // true for enable, false for disable
}

export default function ApiGatewayToggleDialog({
  clusterDetail,
  open,
  setOpen,
  refetchCluster,
  isEnabling,
}: ApiGatewayToggleDialogProps) {
  const { setErrorToast, setSuccessToast } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeDialog = () => {
    setOpen(false);
    setIsSubmitting(false);
  };

  const toggleApiGatewayAction = useActionWithPath({
    onSuccess: () => {
      const action = isEnabling ? "enabled" : "disabled";
      setSuccessToast(
        `API Gateway has been ${action} for ${clusterDetail.name}`
      );
      closeDialog();
      refetchCluster?.();
    },
    onError: async (response: any) => {
      setIsSubmitting(false);
      try {
        const json = await response.json();
        setErrorToast(
          `Could not ${isEnabling ? "enable" : "disable"} API Gateway: ${
            json.message || "Unknown error"
          }`
        );
      } catch (e) {
        setErrorToast(
          `Could not ${isEnabling ? "enable" : "disable"} API Gateway`
        );
      }
      refetchCluster?.();
    },
  });

  const handleToggleApiGateway = () => {
    if (!clusterDetail) return;
    setIsSubmitting(true);

    // @ts-ignore
    toggleApiGatewayAction.mutate({
      method: "post",
      path: `/toggleApiGateway?clusterName=${clusterDetail.name}&enable=${isEnabling}`,
    });
  };

  const getDialogContent = () => {
    if (isEnabling) {
      return {
        title: "Enable API Gateway",
        description:
          "This will deploy API Gateway to your cluster, which includes:",
        features: [
          "InfluxDB deployment for metrics collection",
          "API Gateway ingress configuration",
          "DNS record setup for api.{cluster-domain}",
          "SSL certificate provisioning",
          "Route53 DNS configuration",
        ],
        warning:
          "This operation will create additional cloud resources and may take several minutes to complete.",
        confirmText: "Enable API Gateway",
      };
    } else {
      return {
        title: "Disable API Gateway",
        description: "This will disable API Gateway for your cluster.",
        features: [
          "API Gateway will be marked as disabled",
          "Existing resources will remain but won't be accessible",
          "You can re-enable API Gateway later if needed",
        ],
        warning:
          "API endpoints will no longer be accessible through the gateway.",
        confirmText: "Disable API Gateway",
      };
    }
  };

  const content = getDialogContent();

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={closeDialog}>
      <DialogTitle>{content.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Cluster:</strong> {clusterDetail?.name}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Cloud Provider:</strong> {clusterDetail?.cloudProvider}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {content.description}
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          {content.features.map((feature, index) => (
            <Typography
              component="li"
              variant="body2"
              key={index}
              sx={{ mb: 0.5 }}
            >
              {feature}
            </Typography>
          ))}
        </Box>

        <Alert severity={isEnabling ? "info" : "warning"} sx={{ mb: 2 }}>
          {content.warning}
        </Alert>

        {isEnabling && (
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> API Gateway is available for AWS, Azure,
              and GCP clusters. The deployment will create the necessary secrets
              (AWS SSM / Azure Key Vault / GCP) and infrastructure components.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isEnabling ? "primary" : "warning"}
          onClick={handleToggleApiGateway}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? "Processing..." : content.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
